<template>
  <div class="profile-page-wrapper">
    <!-- Breadcrumb -->
    <div class="breadcrumb-container">
      <router-link to="/binance" class="breadcrumb-link">Home</router-link>
      <span class="breadcrumb-separator">></span>
      <span class="breadcrumb-current">Profile</span>
    </div>

    <!-- Title Header -->
    <div class="profile-header">
      <h1 class="profile-title">Profile</h1>
      <p class="profile-subtitle">View and update your personal information</p>
    </div>

    <!-- Dual Pane Container -->
    <div class="profile-content-container">
      <!-- Sidebar Pane -->
      <div class="profile-sidebar profile-card">
        <div class="sidebar-menu">
          <button 
            class="menu-item" 
            :class="{ active: activeTab === 'info' }"
            @click="activeTab = 'info'"
          >
            <div class="menu-icon-wrapper">
              <i class="fa-solid fa-user"></i>
            </div>
            <span class="menu-label">Profile Information</span>
          </button>

          <button 
            class="menu-item" 
            :class="{ active: activeTab === 'password' }"
            @click="activeTab = 'password'"
          >
            <div class="menu-icon-wrapper">
              <i class="fa-solid fa-lock"></i>
            </div>
            <span class="menu-label">Change Password</span>
          </button>
        </div>

        <div class="sidebar-divider"></div>

        <!-- Account Info section -->
        <div class="sidebar-section">
          <span class="section-title">Account Info</span>
          <div class="info-row">
            <div class="info-label-group">
              <i class="fa-solid fa-user-shield info-icon"></i>
              <span class="info-text">Role</span>
            </div>
            <span class="info-badge">{{ currentUser.role || 'USER' }}</span>
          </div>
        </div>
      </div>

      <!-- Main Form Pane -->
      <div class="profile-main-pane profile-card">
        <!-- Tab 1: Profile Information -->
        <div v-if="activeTab === 'info'" class="tab-pane-content">
          <div class="pane-header">
            <h2 class="pane-title">Profile Information</h2>
            <p class="pane-subtitle">Update your personal information.</p>
          </div>

          <form @submit.prevent="saveProfile" class="profile-form">
            <div class="form-grid">
              <!-- Full Name -->
              <div class="form-group">
                <label for="fullName">Full Name</label>
                <input 
                  id="fullName" 
                  type="text" 
                  v-model="form.fullName" 
                  placeholder="Enter full name"
                  required
                />
              </div>

              <!-- Email -->
              <div class="form-group">
                <label for="email">Email</label>
                <input 
                  id="email" 
                  type="email" 
                  v-model="form.email" 
                  placeholder="admin@example.com"
                />
              </div>

              <!-- Role -->
              <div class="form-group">
                <label for="role">Role</label>
                <input id="role" type="text" :value="currentUser.role" disabled class="disabled-input" />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="form-actions">
              <button type="button" @click="resetProfileForm" class="btn-cancel">
                Cancel
              </button>
              <button type="submit" class="btn-save">
                Save
              </button>
            </div>
          </form>
        </div>

        <!-- Tab 2: Change Password -->
        <div v-if="activeTab === 'password'" class="tab-pane-content">
          <div class="pane-header">
            <h2 class="pane-title">Change Password</h2>
            <p class="pane-subtitle">Ensure your account is using a long, random password to stay secure.</p>
          </div>

          <form @submit.prevent="savePassword" class="profile-form">
            <div class="form-grid-single">
              <!-- Current Password -->
              <div class="form-group">
                <label for="currentPassword">Current Password</label>
                <input 
                  id="currentPassword" 
                  type="password" 
                  v-model="passwordForm.current" 
                  placeholder="••••••••"
                  required
                />
              </div>

              <!-- New Password -->
              <div class="form-group">
                <label for="newPassword">New Password</label>
                <input 
                  id="newPassword" 
                  type="password" 
                  v-model="passwordForm.new" 
                  placeholder="••••••••"
                  required
                />
              </div>

              <!-- Confirm Password -->
              <div class="form-group">
                <label for="confirmPassword">Confirm Password</label>
                <input 
                  id="confirmPassword" 
                  type="password" 
                  v-model="passwordForm.confirm" 
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="form-actions">
              <button type="button" @click="resetPasswordForm" class="btn-cancel">
                Cancel
              </button>
              <button type="submit" class="btn-save">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';
import { getCurrentUser } from '@/services/authStorage';

export default {
  name: 'UserProfile',
  props: {
    initialTab: {
      type: String,
      default: 'info'
    }
  },
  data() {
    const currentUser = getCurrentUser() || {};
    return {
      activeTab: this.initialTab,
      currentUser,
      originalForm: {
        fullName: currentUser.fullName || '',
        email: currentUser.email || ''
      },
      form: {
        fullName: currentUser.fullName || '',
        email: currentUser.email || ''
      },
      passwordForm: {
        current: '',
        new: '',
        confirm: ''
      },
      isSavingProfile: false,
      isChangingPassword: false
    };
  },
  created() {
    this.syncCurrentUser();
  },
  methods: {
    syncCurrentUser() {
      this.currentUser = getCurrentUser() || {};
      this.form = {
        fullName: this.currentUser.fullName || '',
        email: this.currentUser.email || ''
      };
      this.originalForm = { ...this.form };
    },
    async saveProfile() {
      this.isSavingProfile = true;
      try {
        const updatedUser = await AuthService.updateProfile({
          fullName: this.form.fullName,
          email: this.form.email
        });
        this.currentUser = updatedUser;
        this.originalForm = { ...this.form };
        this.$toast.open({
          message: 'Profile information updated successfully.',
          type: 'success',
          position: 'top-right'
        });
      } catch (e) {
        this.$toast.open({
          message: e?.response?.data?.message || 'Failed to save profile details.',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.isSavingProfile = false;
      }
    },
    resetProfileForm() {
      this.form = { ...this.originalForm };
      this.$toast.open({
        message: 'Changes discarded.',
        type: 'info',
        position: 'top-right'
      });
    },
    async savePassword() {
      if (this.passwordForm.new !== this.passwordForm.confirm) {
        this.$toast.open({
          message: 'New password and confirm password do not match!',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      if (this.passwordForm.new.length < 8) {
        this.$toast.open({
          message: 'Password must be at least 8 characters long.',
          type: 'error',
          position: 'top-right'
        });
        return;
      }

      this.isChangingPassword = true;
      try {
        await AuthService.changePassword({
          currentPassword: this.passwordForm.current,
          newPassword: this.passwordForm.new
        });
        this.$toast.open({
          message: 'Password changed successfully.',
          type: 'success',
          position: 'top-right'
        });
        this.resetPasswordForm();
      } catch (e) {
        this.$toast.open({
          message: e?.response?.data?.message || 'Failed to change password.',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.isChangingPassword = false;
      }
    },
    resetPasswordForm() {
      this.passwordForm = {
        current: '',
        new: '',
        confirm: ''
      };
    }
  },
  watch: {
    initialTab(value) {
      this.activeTab = value;
    }
  }
};
</script>

<style scoped>
/* Main Wrapper */
.profile-page-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  text-align: left;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

/* Breadcrumbs */
.breadcrumb-container {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 20px;
}

.breadcrumb-link {
  color: #7c3aed;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.breadcrumb-link:hover {
  opacity: 0.8;
}

.breadcrumb-separator {
  color: #94a3b8;
}

.breadcrumb-current {
  color: #64748b;
}

/* Profile Title Header */
.profile-header {
  margin-bottom: 30px;
}

.profile-title {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.profile-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Page Layout */
.profile-content-container {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  align-items: start;
  /* box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2); */
}

/* Custom Cards */
.profile-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.04);
  padding: 24px;
  transition: all 0.3s ease;
}

/* Sidebar Styling */
.profile-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-icon-wrapper {
  width: 20px;
  display: flex;
  justify-content: center;
  font-size: 16px;
  color: #64748b;
  transition: color 0.2s ease;
}

.menu-label {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  transition: color 0.2s ease;
}

.menu-item:hover {
  background: #f8fafc;
}

.menu-item.active {
  background: #f5f3ff;
}

.menu-item.active .menu-icon-wrapper {
  color: #7c3aed;
}

.menu-item.active .menu-label {
  color: #7c3aed;
}

.sidebar-divider {
  height: 1px;
  background: #e2e8f0;
}

/* Account Info section */
.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #7c3aed;
  letter-spacing: 0.5px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label-group {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 13px;
}

.info-icon {
  font-size: 14px;
}

.info-text {
  font-weight: 500;
}

.info-badge {
  background: #ede9fe;
  color: #7c3aed;
  font-size: 10px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.2px;
}

/* Main Panel Styling */
.profile-main-pane {
  min-height: 480px;
}

.tab-pane-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.pane-header {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 16px;
}

.pane-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.pane-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* Profile Form styling */
.profile-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 24px;
}

.form-grid-single {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 500px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.form-group input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}

.form-group input.disabled-input {
  background: #f8fafc;
  color: #94a3b8;
  border-color: #e2e8f0;
  cursor: not-allowed;
}

/* Custom Select Styling */
.select-wrapper {
  position: relative;
  width: 100%;
}

.select-wrapper select {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.select-wrapper select:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}

.select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #64748b;
  pointer-events: none;
}

/* Action Buttons Container */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #f1f5f9;
  padding-top: 24px;
  margin-top: 12px;
}

/* Buttons */
.btn-cancel {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-save {
  padding: 10px 28px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(63, 129, 229, 0.2);
  transition: all 0.2s ease;
}

.btn-save:hover {
  opacity: 0.95;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(63, 129, 229, 0.3);
}

.btn-save:active {
  transform: translateY(0);
}

/* Theme Global Dark Mode Support */
:global(.dark-mode) .breadcrumb-link {
  color: #a78bfa;
}

:global(.dark-mode) .breadcrumb-separator {
  color: #475569;
}

:global(.dark-mode) .breadcrumb-current {
  color: #94a3b8;
}

:global(.dark-mode) .profile-title {
  color: #f1f5f9;
}

:global(.dark-mode) .profile-subtitle {
  color: #94a3b8;
}

:global(.dark-mode) .profile-card {
  background: #1e2329;
  border-color: rgba(255, 255, 255, 0.06);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

:global(.dark-mode) .menu-label {
  color: #cbd5e1;
}

:global(.dark-mode) .menu-icon-wrapper {
  color: #94a3b8;
}

:global(.dark-mode) .menu-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

:global(.dark-mode) .menu-item.active {
  background: rgba(124, 58, 237, 0.15);
}

:global(.dark-mode) .menu-item.active .menu-label {
  color: #c084fc;
}

:global(.dark-mode) .menu-item.active .menu-icon-wrapper {
  color: #c084fc;
}

:global(.dark-mode) .sidebar-divider {
  background: rgba(255, 255, 255, 0.08);
}

:global(.dark-mode) .info-label-group {
  color: #94a3b8;
}

:global(.dark-mode) .info-badge {
  background: rgba(124, 58, 237, 0.2);
  color: #c084fc;
}

:global(.dark-mode) .pane-header {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .pane-title {
  color: #f1f5f9;
}

:global(.dark-mode) .pane-subtitle {
  color: #94a3b8;
}

:global(.dark-mode) .form-group label {
  color: #cbd5e1;
}

:global(.dark-mode) .form-group input {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .form-group input:focus {
  border-color: #a78bfa;
  box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.15);
}

:global(.dark-mode) .form-group input.disabled-input {
  background: #181a20;
  color: #475569;
  border-color: #2b3139;
}

:global(.dark-mode) .select-wrapper select {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .select-wrapper select:focus {
  border-color: #a78bfa;
}

:global(.dark-mode) .select-arrow {
  color: #94a3b8;
}

:global(.dark-mode) .form-actions {
  border-top-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .btn-cancel {
  background: transparent;
  color: #cbd5e1;
  border-color: #475569;
}

:global(.dark-mode) .btn-cancel:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: #cbd5e1;
}

/* Responsive grid */
@media (max-width: 900px) {
  .profile-content-container {
    grid-template-columns: 1fr;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
